import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { getApiUrl } from "@/utils/api";
import { useTranslation } from "react-i18next";

export default function Update() {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const navigate = useNavigate();
  const { token } = useParams();

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(
        getApiUrl("/password_reset/validate_token/"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token }),
        },
      );

      if (response.ok) {
        setIsValidToken(true);
      } else {
        setError(
          "El enlace para restablecer la contraseña no es válido o ha expirado",
        );
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (err) {
      setError("Ha ocurrido un error al validar el token");
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (password !== passwordRepeat) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(getApiUrl("/password_reset/confirm/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, password }),
      });

      if (response.ok) {
        setMessage("Contraseña actualizada correctamente");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError("Ha ocurrido un error al actualizar la contraseña");
      }
    } catch (err) {
      setError("Ha ocurrido un error. Por favor, inténtalo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-4">
        <p>Validando enlace...</p>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-4">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-4 gap-4">
      <Card className="w-full max-w-md bg-white">
        <div className="flex justify-center py-8">
          <img src={logo} alt="Logo" className="h-16" />
        </div>
      </Card>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            {t("common.update-password")}
          </CardTitle>
          <CardDescription className="text-center">
            {t("common.update-description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="password">Nueva Contraseña</label>
              <Input
                id="password"
                type="password"
                placeholder="Nueva contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="passwordRepeat">Repetir Contraseña</label>
              <Input
                id="passwordRepeat"
                type="password"
                placeholder="Repetir contraseña"
                value={passwordRepeat}
                onChange={(e) => setPasswordRepeat(e.target.value)}
                required
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            {message && <p className="text-green-500 text-sm">{message}</p>}
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
              variant="diagonal"
            >
              {t("common.update")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
